import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { apiService, API_BASE_URL } from '../../services/api';
import { formatAmsterdamDate } from '../../utils/formatAmsterdamDate';
import LocationIcon from '../../components/icons/LocationIcon';
import {Student} from "../../types/portal";
import DateTimePill from '../../components/portal/DateTimePill';
import { useTranslation } from 'react-i18next';
import StarRating from '../../components/common/StarRating';
import Avatar from '../../components/common/Avatar';
import ReviewCard from '../../components/portal/ReviewCard';
import BulkLessonCreateModal from '../../components/portal/teacher/BulkLessonCreateModal';

import LessonCreateModal from '../../components/portal/teacher/LessonCreateModal';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 40px;
  color: #111827;

  @media (max-width: 768px) {
    padding-top: 32px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 2rem);

  @media (max-width: 768px) {
    padding: 1rem var(--mobile-page-padding-x);
    padding-top: var(--mobile-page-padding-top);
  }
`;

const WelcomeSection = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  h1 {
    font-size: 2rem;
    font-weight: 800;
    color: #111827;
    margin: 0;
  }
  p {
    color: #4b5563;
    font-size: 1.1rem;
  }

  .welcome-text {
    flex: 1;
  }

  .action-buttons {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    h1 {
      font-size: 1.5rem;
    }
    p {
      font-size: 1rem;
    }
    
    .action-buttons {
      justify-content: center;
    }
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const AnimatedCard = styled(motion.div)`
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  padding: 1.5rem;
  height: fit-content;

  &.full-width-mobile {
    @media (max-width: 640px) {
      border-radius: 0;
      border-left: none;
      border-right: none;
      padding: 1.25rem 1rem;
      width: 100vw;
      margin-left: calc(-1 * var(--mobile-page-padding-x));
      margin-right: calc(-1 * var(--mobile-page-padding-x));
    }
  }

  &.hide-on-mobile {
    @media (max-width: 640px) {
      display: none;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const StatCard = styled(AnimatedCard)<{ color?: string }>`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #ffffff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  height: auto;
  border-radius: 16px;

  @media (max-width: 480px) {
    padding: 1rem;
  }

  .stat-value {
    font-size: 1.75rem;
    font-weight: 800;
    color: #111827;
    line-height: 1.2;

    @media (max-width: 480px) {
      font-size: 1.5rem;
    }
  }

  .stat-label {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    @media (max-width: 480px) {
      font-size: 0.65rem;
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 0.5rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }

  @media (max-width: 480px) {
    button {
      padding: 0.4rem 0.8rem;
      font-size: 0.75rem;
      flex-shrink: 0;
    }
  }
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 10px;
  }

  @media (max-width: 640px) {
    max-height: none;
    overflow-y: visible;
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 0.75rem;
    /* This allows the carousel to span full device width by counteracting parent paddings */
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scroll-padding: 1rem; /* Ensure snap matches the padding */

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ListItem = styled(motion.div)`
  padding: 0.6rem 0.85rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  margin-bottom: 0.5rem;

  &:hover {
    background: #f8fafc;
    border-color: rgba(236, 92, 44, 0.1);
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  }

  @media (max-width: 640px) {
    flex-shrink: 0;
    width: 85vw;
    max-width: 300px;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 1rem;
    scroll-snap-align: center;
    background: #f9fafb;
    margin-bottom: 0;

    &:hover {
      transform: none;
      box-shadow: none;
    }

    .main-info {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem !important;
    }

    .badge-container {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      padding-top: 0.4rem;
      border-top: 1px solid rgba(0, 0, 0, 0.05);
      
      & > * {
          width: 100%;
          text-align: center;
          justify-content: center;
      }
    }
    
    .date-pill-wrapper {
        width: 100%;
    }
  }
`;


const ActionButton = styled(motion.button)<{ variant?: 'primary' | 'success' | 'danger' | 'outline' }>`
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return 'background: #10b981; color: white;';
      case 'danger':
        return 'background: #ef4444; color: white;';
      case 'outline':
        return 'background: transparent; border: 2px solid #e5e7eb; color: #374151;';
      default:
        return 'background: var(--primary-orange); color: white;';
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Badge = styled.span<{ type?: 'blue' | 'green' | 'orange' | 'red' }>`
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  ${props => {
    switch (props.type) {
      case 'green': return 'background: #dcfce7; color: #065f46;';
      case 'orange': return 'background: #ffedd5; color: #9a3412;';
      case 'red': return 'background: #fee2e2; color: #991b1b;';
      default: return 'background: #dbeafe; color: #1e40af;';
    }
  }}
`;

const EmptyState = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.95rem;
  background: #fcfcfc;
  border-radius: 20px;
  border: 1px dashed rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  &::before {
    content: '🗓️';
    font-size: 2rem;
    opacity: 0.5;
  }
`;

const Select = styled.select`
  padding: 0.55rem 0.75rem; border: 2px solid rgba(102,126,234,0.2); border-radius: 10px;
`;

const ModalBackdrop = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center; z-index: 3000;
`;
const ModalCard = styled.div`
  width: 95%; max-width: 520px; background: #ffffff; border-radius: 16px; box-shadow: 0 20px 60px rgba(0,0,0,0.1); border: 1px solid rgba(0,0,0,0.05); color: #111827;
`;
const ModalHeader = styled.div`
  padding: 1rem 1.25rem; border-bottom: 1px solid rgba(0,0,0,0.05); font-weight: 800; color: #111827;
`;
const ModalBody = styled.div`
  padding: 1rem 1.25rem; display: grid; gap: 0.75rem;
  label { color: #4b5563; font-weight: 700; font-size: 13px; }
  input { background: #f9fafb; border: 1px solid rgba(0,0,0,0.1); border-radius: 10px; padding: 0.75rem; color: #111827; }
`;
const ModalFooter = styled.div`
  padding: 1rem 1.25rem; display: flex; justify-content: flex-end; gap: 0.5rem; border-top: 1px solid rgba(0,0,0,0.05);
`;

function formatTimeHHmm(dateTime: string | undefined) {
  if (!dateTime) return '';
  try {
    return new Date(dateTime).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Amsterdam' });
  } catch { return ''; }
}

const QuickActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    justify-content: center;
    
    button {
        flex: 1;
        width: auto;
        justify-content: center;
        padding: 0.75rem 0.5rem;
        font-size: 0.85rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
  }

  @media (min-width: 641px) and (max-width: 768px) {
    justify-content: center;
  }
`;

const TeacherDashboard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(s => s.auth);
  const { students, teachers } = useAppSelector(s => s.user);
  const navigate = useNavigate();

  const [lessons, setLessons] = useState<any[]>([]);
  const [studentsLocal, setStudentsLocal] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const [saveMsg, setSaveMsg] = useState<string|null>(null);
  // Add Student modal state
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [addForm, setAddForm] = useState<{ firstName: string; lastName: string; email: string }>({ firstName: '', lastName: '', email: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string|null>(null);
  const [addLicenseType, setAddLicenseType] = useState<string>('');
  const [addTeacherLicenses, setAddTeacherLicenses] = useState<string[]>([]);
  const [showResultModal, setShowResultModal] = useState<{ open: boolean; message: string; isError: boolean }>({ open: false, message: '', isError: false });
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isSingleModalOpen, setIsSingleModalOpen] = useState(false);
  const [stats, setStats] = useState<{ completedCount: number; upcomingCount: number; averageStudentRating: number | null; activeStudentsCount?: number; totalReviews?: number }>({ completedCount: 0, upcomingCount: 0, averageStudentRating: 0, totalReviews: 0 });
  const [recentExams, setRecentExams] = useState<any[]>([]);
  const [isShowingPassedExams, setIsShowingPassedExams] = useState(false);
  const [outstandingPayments, setOutstandingPayments] = useState<any[]>([]);
  const [recentReviews, setRecentReviews] = useState<any[]>([]);

  const loadStats = async () => {
    try {
      const resp = await apiService.get<any>(API_ENDPOINTS.LESSONS.STATISTICS_TEACHER);
      if (resp?.data || resp) {
        const data = resp.data || resp;
        setStats({
          averageStudentRating: data.averageStudentRating ?? 0,
          completedCount: data.completedCount ?? 0,
          upcomingCount: data.upcomingCount ?? 0,
          totalReviews: data.totalReviews ?? data.completedCount ?? 0,
          activeStudentsCount: data.activeStudentsCount
        });
      }
    } catch (e) {}
  };

  const loadExams = async () => {
    try {
      const resp = await apiService.get<any[]>(API_ENDPOINTS.EXAMS.TEACHER_ALL);
      if (Array.isArray(resp)) {
        const now = new Date();
        const upcoming = resp
          .filter(e => e.startTime && new Date(e.startTime) >= now)
          .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
        
        if (upcoming.length > 0) {
          setRecentExams(upcoming.slice(0, 5));
          setIsShowingPassedExams(false);
        } else {
          const passed = resp
            .filter(e => e.startTime && new Date(e.startTime) < now)
            .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
          setRecentExams(passed.slice(0, 5));
          setIsShowingPassedExams(passed.length > 0);
        }
      }
    } catch (e) {}
  };

  const loadOutstandingPayments = async () => {
    try {
      const resp = await apiService.get<any[]>(API_ENDPOINTS.PAYMENTS.TEACHER_OUTSTANDING);
      if (Array.isArray(resp)) {
        setOutstandingPayments(resp.slice(0, 5));
      }
    } catch (e) {}
  };

  const loadReviews = async () => {
    try {
      const resp = await apiService.get<any>('/api/lessons/my-teaching?filter=completed');
      const data = Array.isArray(resp?.data) ? resp.data : [];
      const reviews = data
        .filter((l: any) => l.studentRating != null)
        .sort((a: any, b: any) => new Date(b.lessonDateTime || b.dateTime).getTime() - new Date(a.lessonDateTime || a.dateTime).getTime())
        .slice(0, 5);
      setRecentReviews(reviews);
    } catch (e) {}
  };

  useEffect(() => {
    loadStats();
    loadExams();
    loadOutstandingPayments();
    loadReviews();
    loadLessons();
  }, []);

  // Pagination (parity with admin)
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const loadLessons = async () => {
    setLoading(true); setError(null);
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      // Dashboard always shows upcoming booked lessons summary starting from today
      const url = `/api/lessons/my-teaching/page?page=${page}&size=${size}&sortBy=lessonDateTime&sortDir=asc&filter=upcoming_booked&from=${encodeURIComponent(todayStr + 'T00:00:00')}`;
      const resp = await apiService.get<any>(url);
      const list = Array.isArray(resp?.lessons) ? resp.lessons : [];
      const mapped = list.map((l: any) => ({
        id: l.id,
        studentId: l.student ? l.student.id : (l.studentId ?? null),
        teacherId: l.teacher ? l.teacher.id : (l.teacherId ?? null),
        studentFirstName: l.studentFirstName || l.student?.firstName,
        studentName: (() => {
          if (typeof l.studentName === 'string' && l.studentName.trim().length > 0) return l.studentName;
          const s = l.student;
          if (s) {
            const f = (s.firstName ?? '').trim();
            const ls = (s.lastName ?? '').trim();
            if (!f || !ls) return (s.username ?? `${f} ${ls}`.trim());
            return `${f} ${ls}`.trim();
          }
          // try lookup by id later in render
          return undefined;
        })(),
        teacherName: (() => {
          if (typeof l.teacherName === 'string' && l.teacherName.trim().length > 0) return l.teacherName;
          const t = l.teacher;
          if (t) {
            const f = (t.firstName ?? '').trim();
            const ls = (t.lastName ?? '').trim();
            if (!f || !ls) return (t.username ?? `${f} ${ls}`.trim());
            return `${f} ${ls}`.trim();
          }
          return undefined;
        })(),
        dateTime: l.lessonDateTime ?? l.dateTime ?? l.dateTime,
        startTime: l.startTime ?? ((l.lessonDateTime ?? l.dateTime) ? String(l.lessonDateTime ?? l.dateTime).slice(11,16) : undefined),
        duration: l.durationMinutes ?? l.duration ?? l.durationMinutes,
        status: (typeof l.isBooked === 'boolean') ? (l.isBooked ? 'SCHEDULED' : 'AVAILABLE') : (l.status ?? 'SCHEDULED'),
        type: l.lessonType ?? l.type,
        location: l.location ?? (() => {
          if (l.student?.address) {
            const street = String(l.student.address).split(',')[0].trim();
            return street || 'Amsterdam';
          }
          return 'Amsterdam';
        })(),
        notes: l.notes,
      }));
      const sorted = [...mapped].sort((a, b) => {
        const ta = a?.dateTime ? Date.parse(a.dateTime) : Number.MAX_SAFE_INTEGER;
        const tb = b?.dateTime ? Date.parse(b.dateTime) : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      });
      setLessons(sorted);
      setTotalPages(Number(resp?.totalPages ?? 1));
      setTotalItems(Number(resp?.totalItems ?? sorted.length));
    } catch (e1: any) {
      // Fallback to non-paginated endpoint if paginated not available yet
      try {
        const resp2 = await apiService.get<any>('/api/lessons/my-teaching');
        const data = Array.isArray(resp2?.data) ? resp2.data : [];
        const mapped2 = data.map((l: any) => ({
          id: l.id,
          studentId: l.student?.id ?? l.studentId ?? null,
          teacherId: l.teacher?.id ?? l.teacherId ?? null,
          studentFirstName: l.studentFirstName || l.student?.firstName,
          studentName: l.student ? `${l.student.firstName ?? ''} ${l.student.lastName ?? ''}`.trim() : undefined,
          teacherName: l.teacher ? `${l.teacher.firstName ?? ''} ${l.teacher.lastName ?? ''}`.trim() : undefined,
          dateTime: l.lessonDateTime ?? l.dateTime,
          startTime: l.startTime ?? (l.lessonDateTime ? String(l.lessonDateTime).slice(11,16) : undefined),
          duration: l.durationMinutes ?? l.duration,
          status: typeof l.isBooked === 'boolean' ? (l.isBooked ? 'SCHEDULED' : 'AVAILABLE') : (l.status ?? 'SCHEDULED'),
          type: l.lessonType ?? l.type,
          location: (() => {
            if (l.student?.address) {
              const street = String(l.student.address).split(',')[0].trim();
              return street || 'Amsterdam';
            }
            return 'Amsterdam';
          })(),
          notes: l.notes,
        }));
        const sorted2 = [...mapped2].sort((a, b) => {
          const ta = a?.dateTime ? Date.parse(a.dateTime) : Number.MAX_SAFE_INTEGER;
          const tb = b?.dateTime ? Date.parse(b.dateTime) : Number.MAX_SAFE_INTEGER;
          return ta - tb;
        });
        setLessons(sorted2);
        setTotalPages(1);
        setTotalItems(sorted2.length);
      } catch (e2: any) {
        setError(e2?.message || e1?.message || 'Failed to load lessons');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLessons();
  }, [page, size]);

  // Load students locally for teachers if store is empty (teacher-accessible endpoint)
  useEffect(() => {
    const loadStudents = async () => {
      try {
        if (students && students.length > 0) return; // already in store (e.g., admin session)
        const resp = await apiService.get<any>('/api/lessons/students');
        const list = Array.isArray(resp?.data) ? resp.data : [];
        const mapped = list.map((u: any) => ({
          id: u.id,
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          username: u.username,
          address: u.address,
          assignedTeacher: u.assignedTeacher?.id || u.assignedTeacherId,
          isActive: u.enabled === true
        }));
        setStudentsLocal(mapped);
      } catch (e) {
        // ignore silently
      }
    };
    loadStudents();
  }, [students]);

  // Removed inline edit/cancel modal in favor of navigating to Teacher Lesson Detail page.

  // Add Student modal handlers
  const validateAddForm = (): string | null => {
    if (!addForm.firstName || addForm.firstName.trim().length < 1) return 'First name is required';
    if (!addForm.lastName || addForm.lastName.trim().length < 1) return 'Last name is required';
    const email = addForm.email?.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address';
    if (!addLicenseType) return 'Please select a license type';
    return null;
  };

  const submitAddStudent = async () => {
    const validationError = validateAddForm();
    if (validationError) { setAddError(validationError); return; }
    setAddLoading(true);
    setAddError(null);
    try {
      const body: any = { firstName: addForm.firstName.trim(), lastName: addForm.lastName.trim(), email: addForm.email.trim(), password: 'temporary', licenseType: addLicenseType };
      const resp = await apiService.post<any>('/api/auth/create-student', body);
      setShowAddStudentModal(false);
      setShowResultModal({ open: true, message: resp?.message || 'Student created. Set-password email sent.', isError: false });
      setSaveMsg(resp?.message || 'Student created.');
      loadStats();
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (e: any) {
      const msg = e?.message || 'Failed to create student';
      setShowResultModal({ open: true, message: msg, isError: true });
    } finally { setAddLoading(false); }
  };

  const closeAddStudentModal = () => { if (!addLoading) setShowAddStudentModal(false); };
  const closeResultModal = () => setShowResultModal({ open: false, message: '', isError: false });

  const mergedStudents = (students && students.length > 0) ? students : studentsLocal;
  // Only allow teacher to select from students assigned to them
  const myStudents = useMemo(() => {
    const meId = user?.id != null ? String(user.id) : '';
    return (mergedStudents || []).filter((s: any) => String(s.assignedTeacher) === meId || String(s.assignedTeacherId) === meId);
  }, [mergedStudents, user]);
  const studentsOptions = useMemo(() => myStudents.map((s:any) => ({ id: s.id, label: `${s.firstName || s.username} ${s.lastName || ''}`.trim(), address: s.address })), [myStudents]);

  const filteredLessons = useMemo(() => {
    const now = new Date();
    // Default to upcoming booked lessons as the filters are removed from UI
    return lessons
      .filter((l: any) => !!l.studentId && new Date(l.dateTime) >= now)
      .sort((a: any, b: any) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime())
      .slice(0, 2); // show up to 2
  }, [lessons]);

  // Stats derived from server data (no hardcoded numbers)
  const activeStudentsCount = useMemo(() => {
    return myStudents.filter((s: any) => s?.isActive !== false).length;
  }, [myStudents]);

  const experienceYears = useMemo(() => {
    // Prefer explicit experience field if provided by backend; fallback to years since createdAt
    const explicit = (user as any)?.experience;
    if (typeof explicit === 'number' && explicit >= 0) return explicit;
    if (!user?.createdAt) return 0;
    const started = new Date(user.createdAt).getTime();
    const now = Date.now();
    const years = Math.floor((now - started) / (365.25 * 24 * 60 * 60 * 1000));
    return Math.max(0, years);
  }, [user?.createdAt, (user as any)?.experience]);

  if (!user) return null;
  if (user.role !== 'teacher') return <div>{t('portal.common.not_authorized', 'Not authorized')}</div>;

  return (
    <DashboardContainer>
      <ContentWrapper>
        <WelcomeSection>
          <div className="welcome-text">
            <h1>{t('portal.teacher.dashboard.welcome', 'Welcome back, {{name}}!', { name: user.firstName || user.username })}</h1>
          </div>
          <div className="action-buttons">
            <ActionButton
              variant="outline"
              onClick={() => setIsSingleModalOpen(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ padding: '0.6rem 1.2rem', fontSize: '0.9rem' }}
            >
              + {t('portal.lessons.create.title', 'Add Lesson')}
            </ActionButton>
            <ActionButton
              variant="outline"
              onClick={() => setIsBulkModalOpen(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ padding: '0.6rem 1.2rem', fontSize: '0.9rem' }}
            >
              + {t('portal.lessons.bulk_create.title')}
            </ActionButton>
            <ActionButton
              variant="primary"
              onClick={() => { setAddForm({ firstName: '', lastName: '', email: '' }); setAddLicenseType(''); setAddError(null); setShowAddStudentModal(true); }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ padding: '0.6rem 1.2rem', fontSize: '0.9rem' }}
            >
              + {t('portal.admin.students.add_button', 'Add Student')}
            </ActionButton>
          </div>
        </WelcomeSection>

        <MainGrid>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <AnimatedCard className="full-width-mobile" style={{ height: 'auto' }}>
              <SectionHeader>
                <h3>{t('portal.teacher.dashboard.upcoming_lessons_title', 'Upcoming Lessons')}</h3>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <ActionButton variant="outline" onClick={() => navigate('/portal/lesson')}>{t('portal.common.view_all', 'View All')}</ActionButton>
                </div>
              </SectionHeader>
              {loading ? (
                <EmptyState>{t('portal.lessons.loading', 'Loading lessons…')}</EmptyState>
              ) : filteredLessons.length === 0 ? (
                <EmptyState>{t('portal.lessons.none_upcoming', 'No upcoming lessons scheduled.')}</EmptyState>
              ) : (
                <ListContainer>
                  {filteredLessons.map((l) => (
                    <ListItem
                      key={l.id}
                      onClick={() => navigate(`/portal/lesson/${l.id}`)}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      <div className="main-info" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div className="date-pill-wrapper">
                          <DateTimePill dateTime={l.dateTime} startTime={l.startTime} style={{ width: '100%', minWidth: 120 }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '1rem' }}>{l.studentFirstName || l.studentName || (t('portal.admin.students.not_assigned', 'Not Assigned') as any)}</div>
                          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                            {l.type} • {l.duration} min
                          </div>
                        </div>
                      </div>
                      <div className="badge-container">
                        <Badge type="blue">{t('portal.common.booked', 'Booked') as any}</Badge>
                      </div>
                    </ListItem>
                  ))}
                </ListContainer>
              )}
            </AnimatedCard>

            <StatsGrid style={{ margin: 0, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
              <StatCard>
                <div className="stat-value">{stats.completedCount}</div>
                <div className="stat-label">{t('portal.admin.teachers.completed_lessons', 'Lessons Completed') as any}</div>
              </StatCard>
              <StatCard>
                <div className="stat-value">{stats.upcomingCount}</div>
                <div className="stat-label">{t('portal.teacher.dashboard.upcoming_lessons_stat', 'Upcoming Lessons') as any}</div>
              </StatCard>
              <StatCard>
                <div className="stat-value">{stats.activeStudentsCount ?? activeStudentsCount}</div>
                <div className="stat-label">{t('portal.teacher.dashboard.active_students_stat', 'Active Students') as any}</div>
              </StatCard>
              <StatCard>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                   <StarRating value={stats.averageStudentRating || 0} readOnly size={16} />
                   <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>
                     ({stats.totalReviews || 0} {t('portal.common.reviews', 'reviews')})
                   </span>
                </div>
                <div className="stat-label">{t('portal.teacher.dashboard.avg_rating_stat', 'Avg. Rating') as any}</div>
              </StatCard>
            </StatsGrid>

            <AnimatedCard className="full-width-mobile">
              <SectionHeader>
                <h3>{t('portal.teacher.dashboard.recent_reviews_title', 'Recent Student Reviews')}</h3>
                <ActionButton 
                  variant="outline" 
                  style={{ padding: '0.35rem 0.75rem', borderRadius: '8px', fontSize: '0.75rem' }}
                  onClick={() => navigate(`/portal/teacher/${user?.id}/reviews`)}
                >
                  {t('portal.teacher.dashboard.view_all_reviews', 'View All')}
                </ActionButton>
              </SectionHeader>
              {recentReviews.length === 0 ? (
                <EmptyState>{t('portal.teacher.dashboard.no_reviews', 'No student reviews yet.')}</EmptyState>
              ) : (
                <ListContainer style={{ overflowY: 'visible', maxHeight: 'none', paddingRight: 0 }}>
                  {recentReviews.map((review, idx) => (
                    <ReviewCard 
                      key={review.id}
                      review={review}
                      idx={idx}
                      onClick={() => {
                        const lessonId = review.lessonId || review.id;
                        navigate(`/portal/lesson/${lessonId}`);
                      }}
                    />
                  ))}
                </ListContainer>
              )}
            </AnimatedCard>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <AnimatedCard className="full-width-mobile" style={{ height: 'auto' }}>
              <SectionHeader>
                <h3>{t('portal.teacher.dashboard.outstanding_payments_title', 'Openstaande betalingen')}</h3>
              </SectionHeader>
              {outstandingPayments.length === 0 ? (
                <EmptyState>{t('portal.teacher.dashboard.payments_verified', 'All payments verified!')}</EmptyState>
              ) : (
                <ListContainer>
                  {outstandingPayments.map((p) => (
                    <ListItem key={p.studentId} onClick={() => navigate('/portal/teacher/payments')}>
                      <div className="main-info" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: 12,
                          background: 'rgba(239, 68, 68, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.25rem'
                        }}>
                          💰
                        </div>
                        <div>
                          <div style={{ fontWeight: 700 }}>{p.studentFirstName}</div>
                          <div style={{ fontSize: '0.85rem', color: '#ef4444', fontWeight: 600 }}>
                            €{p.amount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <div className="badge-container">
                        <Badge type="red">{t('portal.teacher.dashboard.payment_due', 'Outstanding') as any}</Badge>
                      </div>
                    </ListItem>
                  ))}
                </ListContainer>
              )}
            </AnimatedCard>

            <AnimatedCard className="full-width-mobile" style={{ height: 'auto' }}>
              <SectionHeader>
                <h3>
                  {isShowingPassedExams 
                    ? t('portal.teacher.dashboard.passed_exams_title', 'Passed Exams')
                    : t('portal.teacher.dashboard.upcoming_exams_title', 'Upcoming Exams')
                  }
                </h3>
              </SectionHeader>
              {recentExams.length === 0 ? (
                <EmptyState>{t('portal.exams.none', 'No exams scheduled for your students.')}</EmptyState>
              ) : (
                <ListContainer>
                  {recentExams.map((exam) => (
                    <ListItem key={exam.id} onClick={() => navigate(`/portal/teacher/exams/${exam.id}`)}>
                      <div className="main-info" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div className="date-pill-wrapper">
                          <DateTimePill dateTime={exam.startTime} style={{ width: '100%', minWidth: 120 }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700 }}>{exam.student?.firstName}</div>
                          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                            {exam.type}
                          </div>
                        </div>
                      </div>
                      <div className="badge-container">
                        <Badge type={exam.status === 'PASSED' ? 'green' : exam.status === 'FAILED' ? 'red' : 'orange'}>
                          {(() => {
                            const now = new Date();
                            const dt = exam.startTime ? new Date(exam.startTime) : null;
                            if (dt && dt > now) return '—';
                            return t(`portal.exams.status.${(exam.status || 'pending').toLowerCase()}`, exam.status || 'Pending');
                          })() as any}
                        </Badge>
                      </div>
                    </ListItem>
                  ))}
                </ListContainer>
              )}
            </AnimatedCard>
          </div>
        </MainGrid>

      </ContentWrapper>

      <AnimatePresence>
        {saveMsg && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              background: '#10b981',
              color: 'white',
              padding: '1rem 1.5rem',
              borderRadius: '16px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              zIndex: 3000,
              fontWeight: 700
            }}
          >
            ✅ {saveMsg}
          </motion.div>
        )}
      </AnimatePresence>

      <BulkLessonCreateModal
        open={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        onSuccess={() => {
          setSaveMsg(t('portal.lessons.bulk_create.success'));
          loadLessons();
          loadStats();
          setTimeout(() => setSaveMsg(null), 3000);
        }}
      />

      <LessonCreateModal
        open={isSingleModalOpen}
        onClose={() => setIsSingleModalOpen(false)}
        onSuccess={() => {
          setSaveMsg(t('portal.lessons.create.success', 'Lesson created successfully'));
          loadLessons();
          loadStats();
          setTimeout(() => setSaveMsg(null), 3000);
        }}
      />

      <AnimatePresence>
        {showAddStudentModal && (
          <ModalBackdrop onClick={closeAddStudentModal}>
            <ModalCard onClick={e => e.stopPropagation()}>
              <ModalHeader>{t('portal.admin.students.add_modal_title', 'Add New Student')}</ModalHeader>
              <ModalBody>
                {addError && <div style={{ color: '#ef4444', fontSize: '14px', fontWeight: 600 }}>⚠️ {addError}</div>}
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label>{t('portal.register.first_name', 'First Name')}</label>
                    <input
                      type="text"
                      placeholder={String(t('portal.admin.students.first_name_placeholder', 'First Name'))}
                      value={addForm.firstName}
                      onChange={e => setAddForm({ ...addForm, firstName: e.target.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label>{t('portal.register.last_name', 'Last Name')}</label>
                    <input
                      type="text"
                      placeholder={String(t('portal.admin.students.last_name_placeholder', 'Last Name'))}
                      value={addForm.lastName}
                      onChange={e => setAddForm({ ...addForm, lastName: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label>{t('portal.register.email', 'Email Address')}</label>
                  <input
                    type="email"
                    placeholder={String(t('portal.admin.students.email_placeholder', 'student@example.com'))}
                    value={addForm.email}
                    onChange={e => setAddForm({ ...addForm, email: e.target.value })}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label>{t('portal.register.license_type', 'License Type')}</label>
                  <Select
                    value={addLicenseType}
                    onChange={(e: any) => setAddLicenseType(e.target.value)}
                  >
                    <option value="">{t('portal.admin.students.select_type', 'Select Type')}</option>
                    <option value="AM">{t('portal.register.license_am')}</option>
                    <option value="A1">{t('portal.register.license_a1')}</option>
                    <option value="A2">{t('portal.register.license_a2')}</option>
                    <option value="A">{t('portal.register.license_a')}</option>
                    <option value="B_MANUAL">{t('portal.register.license_b_manual')}</option>
                    <option value="B_AUTOMATIC">{t('portal.register.license_b_automatic')}</option>
                    <option value="BE">{t('portal.register.license_be')}</option>
                    <option value="B_PLUS">{t('portal.register.license_b_plus')}</option>
                    <option value="C1">{t('portal.register.license_c1')}</option>
                    <option value="C1E">{t('portal.register.license_c1e')}</option>
                    <option value="C">{t('portal.register.license_c')}</option>
                    <option value="CE">{t('portal.register.license_ce')}</option>
                    <option value="D1">{t('portal.register.license_d1')}</option>
                    <option value="D1E">{t('portal.register.license_d1e')}</option>
                    <option value="D">{t('portal.register.license_d')}</option>
                    <option value="DE">{t('portal.register.license_de')}</option>
                    <option value="T">{t('portal.register.license_t')}</option>
                  </Select>
                </div>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '0.5rem' }}>
                  {t('portal.teacher.dashboard.add_student_hint', 'A temporary password and verification email will be sent to the student.')}
                </p>
              </ModalBody>
              <ModalFooter>
                <ActionButton variant="outline" onClick={closeAddStudentModal} disabled={addLoading}>
                  {t('portal.common.cancel', 'Cancel')}
                </ActionButton>
                <ActionButton variant="primary" onClick={submitAddStudent} disabled={addLoading}>
                  {addLoading ? t('portal.common.creating', 'Creating...') : t('portal.admin.students.create_button', 'Create Student')}
                </ActionButton>
              </ModalFooter>
            </ModalCard>
          </ModalBackdrop>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showResultModal.open && (
          <ModalBackdrop onClick={closeResultModal}>
            <ModalCard onClick={e => e.stopPropagation()}>
              <ModalHeader>{showResultModal.isError ? t('portal.common.error', 'Error') : t('portal.common.success', 'Success')}</ModalHeader>
              <ModalBody>
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{showResultModal.isError ? '⚠️' : '✅'}</div>
                  <p style={{ fontWeight: 600 }}>{showResultModal.message}</p>
                </div>
              </ModalBody>
              <ModalFooter>
                <ActionButton variant="primary" onClick={closeResultModal}>
                  {t('portal.common.ok', 'OK')}
                </ActionButton>
              </ModalFooter>
            </ModalCard>
          </ModalBackdrop>
        )}
      </AnimatePresence>
    </DashboardContainer>
  );
};

export default TeacherDashboard;
